import { useMutation, useQuery } from '@apollo/client';
import { Combobox, Transition } from '@headlessui/react';
import {
  CalendarDaysIcon,
  CheckIcon,
  ChevronDownIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/nextjs';
import { format, getTime } from 'date-fns';
import { subDays } from 'date-fns';
import { cn } from 'lib/utils';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';

import Loader from 'components/atoms/loader';
import Editor from 'components/editor';
import { TimePicker } from 'components/ui/time-picker';

import {
  addTask,
  addTaskComment,
  deleteTaskAssigneesByTaskId,
  getTaskById,
  getTaskComments,
  insertTaskAssignee,
  updateTaskById,
  updateTaskCommentById,
} from 'queries/tasks';

import { useOrganization } from 'providers/organization';
import { useSession } from 'providers/session';
import { useUI } from 'providers/ui';

// @ts-ignore
const TimeAgo = dynamic(() => import('react-timeago'), {
  ssr: false,
});

type Props = {
  taskID: string;
  close: () => void;
  refetchTasks?: () => void;
};

export default function TaskDetail({ taskID, close, refetchTasks }: Props) {
  const ui = useUI();

  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState(false);

  const org = useOrganization();
  const session = useSession();

  const commentEditorRef = useRef(null);
  const [createTaskComment] = useMutation(addTaskComment);
  const [editTaskComment] = useMutation(updateTaskCommentById);
  const { data: singleTaskData, loading: loadingTask } = useQuery(getTaskById, {
    variables: {
      id: taskID,
    },
    fetchPolicy: 'network-only',
  });

  const { data: taskCommentsResponse } = useQuery(getTaskComments, {
    variables: {
      id: taskID,
    },
    fetchPolicy: 'cache-and-network',
  });

  let taskComments = taskCommentsResponse && taskCommentsResponse.task_comment;

  const [createTask] = useMutation(addTask);
  const [editTask] = useMutation(updateTaskById);
  const [deleteTaskAssignees] = useMutation(deleteTaskAssigneesByTaskId);
  const [insertTaskAssigneeMutation] = useMutation(insertTaskAssignee);

  const [error, setError] = useState(false);

  const textInputClass =
    'appearance-none block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm font-light bg-slate-50 focus:bg-white focus:outline-none focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50';

  const singleTask = singleTaskData && singleTaskData.task;

  let isOwned;

  if (singleTask && singleTask?.creator?.id) {
    isOwned = singleTask.creator.id === session.claims['x-hasura-user-id'];
  } else {
    isOwned = 'NEW';
  }

  // const isOwned =
  //   singleTask && singleTask.creator.id === session.claims["x-hasura-user-id"];

  const className =
    textInputClass +
    (error ? ' text-red-500 border-purple-500 font-medium' : '');

  const dueDate = new Date(singleTask && singleTask.due);
  const dueTime =
    singleTask && format(getTime(new Date(singleTask.due)), 'hh:mm');

  const priorityArray = [
    {
      value: 3,
      displayText: 'Low',
    },
    {
      value: 2,
      displayText: 'Medium',
    },
    {
      value: 1,
      displayText: 'High',
    },
  ];

  const typeArray = [
    { value: 'daily', displayText: 'Daily' },
    { value: 'weekly', displayText: 'Weekly' },
    { value: 'monthly', displayText: 'Monthly' },
  ];

  const statusArray = [
    { value: 'TODO', displayText: 'Todo' },
    { value: 'IN_PROGRESS', displayText: 'In Progress' },
    { value: 'IN_REVIEW', displayText: 'In Review' },
    { value: 'COMPLETE', displayText: 'Complete' },
  ];

  const {
    register: registerA,
    handleSubmit: handleSubmitA,
    setValue: setValueA,
    reset: resetA,
    watch: watchA,
    control: controlA,
    getValues: getValuesA,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      dueDate: '',
      dueTime: '',
      member: '',
      members: [] as string[],
      data: null,
      status: 'TODO',
      priority: 3,
      title: '',
      content: '',
      type: '',
    },
  });

  const {
    register: registerB,
    handleSubmit: handleSubmitB,
    reset: resetB,
    setValue: setValueB,
    formState,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      comment: '',
    },
  });

  useEffect(() => {
    if (singleTask && dueDate) {
      setValueA('title', singleTask.title);
      setValueA('content', singleTask.content);

      setValueA('data', singleTask.data);
      setValueA('dueDate', format(dueDate, 'yyyy-MM-dd'));
      // setValueA('targetDate', format(singleTask?.targetDate, 'yyyy-MM-dd'));
      setValueA('dueTime', dueTime);
      const effectiveIds =
        singleTask.task_assignees?.length > 0
          ? singleTask.task_assignees.map(
              (ta: { user_id: string }) => ta.user_id,
            )
          : singleTask.assignee?.id
            ? [singleTask.assignee.id]
            : [];
      setValueA('members', effectiveIds);
      setValueA('member', effectiveIds[0] ?? '');
      // @ts-ignore
      setValueA('status', singleTask.status === 'COMPLETE' ? true : false);
      setValueA('priority', singleTask.priority);
    } else {
      setValueA('dueDate', format(new Date(), 'yyyy-MM-dd'));
    }
  }, [singleTask]);

  useEffect(() => {
    if (singleTask) {
      commentEditorRef?.current?.setData(
        singleTask?.data ?? singleTask?.content,
      );
    }
  }, [commentEditorRef, singleTask]);

  useEffect(() => {
    //@ts-ignore
    if (newComment && newComment.content && newComment.id) {
      //@ts-ignore
      setValueB('comment', newComment.content);
    } else {
      setValueB('comment', '');
    }
  }, [newComment]);

  const editComment = async (data, existing) => {
    // return;

    // Update existing comment
    if (existing && existing.content && existing.id) {
      // return
      if (!data.comment) return;
      try {
        setSaving(true);
        await editTaskComment({
          variables: {
            id: existing.id,
            data: {
              content: data.comment,
            },
          },
          refetchQueries: [
            {
              query: getTaskComments,
              variables: {
                id: taskID,
              },
            },
          ],
        });
        resetB();
        refetchTasks && refetchTasks();
        setSaving(false);
        setNewComment(false);
        ui.setToast(true, {
          type: 'success',
          title: 'Note updated',
        });
      } catch (err) {
        setSaving(false);
        ui.setToast(true, {
          type: 'error',
          title: 'Error updating comment',
        });
        Sentry.captureException(err);
      }
    } else {
      // Add new comment
      if (!data.comment) return;
      try {
        setSaving(true);

        const payload = {
          variables: {
            data: {
              task_id: taskID,
              content: data.comment,
            },
          },
          refetchQueries: [
            {
              query: getTaskComments,
              variables: {
                id: taskID,
              },
            },
          ],
        };

        await createTaskComment(payload);
        refetchTasks && refetchTasks();
        resetB();
        setSaving(false);
        setNewComment(false);
        ui.setToast(true, {
          type: 'success',
          title: 'New comment added',
        });
      } catch (err) {
        setSaving(false);
        ui.setToast(true, {
          type: 'error',
          title: 'Error adding comment',
        });
        Sentry.captureException(err);
      }
    }
  };

  const submit = async (data) => {
    setSaving(true);
    const memberIds: string[] = Array.isArray(data.members)
      ? data.members
      : data.member
        ? [data.member]
        : [];
    const primaryUserId = memberIds[0] ?? null;
    try {
      if (singleTask) {
        try {
          await editTask({
            variables: {
              id: singleTask.id,
              data: {
                title: data.title,
                data: data.data,
                due: new Date(Date.parse(data.dueDate + 'T' + data.dueTime)),
                priority: data.priority,
                type: data?.type,
                user_id: primaryUserId,
              },
            },
            refetchQueries: [
              {
                query: getTaskById,
                variables: { id: taskID },
              },
            ],
          });
          await deleteTaskAssignees({
            variables: { task_id: singleTask.id },
          });
          for (const userId of memberIds) {
            await insertTaskAssigneeMutation({
              variables: { task_id: singleTask.id, user_id: userId },
            });
          }
          resetA();
          refetchTasks && refetchTasks();
          ui.setToast(true, {
            type: 'success',
            title: 'Task updated',
            message: 'Task has been updated successfully',
          });
        } catch (err) {
          ui.setToast(true, {
            type: 'error',
            title: 'Error updating task',
            message: err?.message ?? 'Please try again',
          });
        }
      } else {
        try {
          const result = await createTask({
            variables: {
              data: {
                title: data.title,
                content: data.content,
                data: data.data,
                due: new Date(Date.parse(data.dueDate + 'T' + data.dueTime)),
                status: 'TODO',
                user_id: primaryUserId,
                priority: data.priority,
                type: data?.type,
              },
            },
            refetchQueries: [
              {
                query: getTaskById,
                variables: { id: taskID },
              },
            ],
          });
          const newTaskId = result?.data?.insert_task_one?.id;
          if (newTaskId && memberIds.length > 0) {
            for (const userId of memberIds) {
              await insertTaskAssigneeMutation({
                variables: { task_id: newTaskId, user_id: userId },
              });
            }
          }
          resetA();
          refetchTasks && refetchTasks();
          ui.setToast(true, {
            type: 'success',
            title: 'Task saved',
            message: 'Task has been saved successfully',
          });
        } catch (err) {
          ui.setToast(true, {
            type: 'error',
            title: 'Error saving task',
            message: err?.message ?? 'Please try again',
          });
        }
      }
    } catch (err) {
      ui.setToast(true, {
        type: 'error',
        title: 'Error saving task',
        message: err?.message ?? 'Please try again',
      });
    }
    setTimeout(() => {
      close();
    }, 500);

    setSaving(false);
  };

  const teamMembers = useMemo(() => {
    return org && org?.team && org.team.length > 0
      ? org.team
          .filter((x) => x.active)
          .map((member) => ({
            id: member.user?.id,
            given_name: member.user?.name,
            family_name: member.user?.surname,
            avatar: member.user?.avatar?.[0]?.asset?.url,
          }))
      : [];
  }, [org]);

  const filteredPeople = useMemo(() => {
    return query === ''
      ? teamMembers
      : teamMembers.filter(
          (person) =>
            person?.given_name
              .toLowerCase()
              .replace(/\s+/g, '')
              .includes(query.toLowerCase().replace(/\s+/g, '')) ||
            person?.family_name
              .toLowerCase()
              .replace(/\s+/g, '')
              .includes(query.toLowerCase().replace(/\s+/g, '')),
        );
  }, [query, teamMembers]);

  if (!singleTask && taskID) {
    return null;
  }

  const annualLeaveRoute =
    singleTask?.metadata?.workflow === 'annual_leave' &&
    typeof singleTask?.metadata?.route === 'string' &&
    singleTask.metadata.route.startsWith('/')
      ? singleTask.metadata.route
      : null;

  const P = ({ node, isOwned, ...props }) => {
    return (
      <p
        className={`mb-2 ${isOwned ? 'pr-12' : ''} font-light leading-relaxed`}
        {...props}
      />
    );
  };

  return (
    <form onSubmit={handleSubmitA(submit)} className="flex flex-col flex-1">
      {annualLeaveRoute ? (
        <div className="mb-5 flex flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <CalendarDaysIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
            <div>
              <div className="text-sm font-semibold text-emerald-900">
                Annual leave decision required
              </div>
              <div className="mt-1 text-xs text-emerald-700">
                Open the request to review notice, balance and rota cover before
                approving or rejecting it.
              </div>
            </div>
          </div>
          <Link
            href={annualLeaveRoute}
            onClick={close}
            className="inline-flex shrink-0 items-center justify-center rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
          >
            Review request
          </Link>
        </div>
      ) : null}
      <div className="grid grid-cols-6 gap-12 flex-1">
        <div className="col-span-4 flex flex-col">
          <input
            type="text"
            disabled={isOwned !== 'NEW' && !isOwned}
            {...registerA('title', { required: true })}
            className={`font-semibold text-xl border-b border-gray-300 py-1 px-2 rounded bg-white focus:outline-none focus:ring-purple-500 focus:border-purple-500 w-full mt-1 disabled:opacity-50`}
            placeholder="Task title"
          />
          {singleTask?.creator && (
            <p className="text-xs text-light mb-4 text-gray-600 pl-1">
              Created by:{' '}
              {singleTask.creator.given_name +
                ' ' +
                singleTask.creator.family_name}
            </p>
          )}
          <div className="my-2 flex-1 flex flex-col">
            <p className="font-semibold mb-2 text-md">Description</p>
            {!loadingTask &&
            singleTask &&
            (singleTask.data ?? singleTask?.content) ? (
              <Editor
                ref={commentEditorRef}
                content={singleTask.data ?? singleTask?.content}
                taskId={taskID}
                onUpdate={(json) => setValueA('data', json)}
              />
            ) : (
              <Editor
                ref={commentEditorRef}
                taskId={taskID}
                onUpdate={(json) => setValueA('data', json)}
              />
            )}
          </div>
        </div>
        <div className="col-span-2 flex flex-col">
          <div>
            <span className="text-sm block mt-2">Priority:</span>
            <select
              disabled={isOwned !== 'NEW' && !isOwned}
              className={`text-sm cursor-pointer rounded-md border-gray-300 w-full bg-white disabled:opacity-50focus:ring-purple-500 sm:text-sm font-light focus:outline-none focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50`}
              {...registerA('priority', { required: true })}
            >
              {priorityArray.map((priority) => (
                <option key={priority.value} value={priority.value}>
                  {priority.displayText}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 space-x-4 mt-2">
            <label className="block">
              <span className="text-sm block mb-1">Due date:</span>
              <input
                disabled={isOwned !== 'NEW' && !isOwned}
                type="date"
                {...registerA('dueDate', { required: true })}
                name="dueDate"
                className={className}
              />
            </label>
            <label className="block">
              <span className="text-sm block mb-1">Due time:</span>
              <TimePicker
                disabled={isOwned !== 'NEW' && !isOwned}
                {...registerA('dueTime', { required: true })}
                name="dueTime"
                className={className}
              />
            </label>
          </div>

          <div className="mb-4 flex-1">
            <div className="grid grid-cols-1 mt-2">
              <div>
                <span className="text-sm block mb-1">Assigned to:</span>

                <Controller
                  control={controlA}
                  name="members"
                  render={({ field: { onChange, value } }) => {
                    const memberIds: string[] = Array.isArray(value)
                      ? value
                      : [];
                    const selectedMembers = memberIds
                      .map((id) => teamMembers?.find((m) => m.id === id))
                      .filter(Boolean);
                    const addMember = (person: { id: string } | null) => {
                      if (person && !memberIds.includes(person.id)) {
                        onChange([...memberIds, person.id]);
                      }
                    };
                    const removeMember = (id: string) => {
                      onChange(memberIds.filter((mid) => mid !== id));
                    };
                    return (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {selectedMembers.map(
                            (person: {
                              id: string;
                              given_name?: string;
                              family_name?: string;
                              avatar?: string;
                            }) => (
                              <div
                                key={person.id}
                                className="flex items-center gap-1.5 rounded-md border border-gray-200 py-1.5 pl-2 pr-1 bg-gray-50"
                              >
                                {person?.avatar ? (
                                  <img
                                    src={person.avatar}
                                    className="h-6 w-6 rounded-full flex-shrink-0"
                                    alt=""
                                  />
                                ) : (
                                  <UserCircleIcon className="h-6 w-6 flex-shrink-0 text-gray-400" />
                                )}
                                <span className="text-sm truncate max-w-[120px]">
                                  {person?.given_name} {person?.family_name}
                                </span>
                                {!(isOwned !== 'NEW' && !isOwned) && (
                                  <button
                                    type="button"
                                    onClick={() => removeMember(person.id)}
                                    className="rounded p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                    aria-label="Remove"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                        {!(isOwned !== 'NEW' && !isOwned) && (
                          <Combobox
                            value={selectedMembers}
                            multiple
                            onChange={(newSelected: { id: string }[]) => {
                              const added = newSelected?.find(
                                (s) => !memberIds.includes(s.id),
                              );
                              if (added) addMember(added);
                            }}
                          >
                            <div className="relative mt-1">
                              <div className="relative w-full cursor-default overflow-hidden rounded-md border border-gray-300 bg-white text-left shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
                                <Combobox.Input
                                  className="text-sm cursor-pointer rounded-md w-full bg-white sm:text-sm font-light border-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-100 disabled:opacity-50"
                                  displayValue={() => 'Add team member...'}
                                  onChange={(event) =>
                                    setQuery(event.target.value)
                                  }
                                />
                                <Combobox.Button className="absolute inset-y-0 top-0 right-0 flex items-center pr-2">
                                  <ChevronDownIcon
                                    className="h-4 w-4 text-gray-600"
                                    strokeWidth={2}
                                    aria-hidden="true"
                                  />
                                </Combobox.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setQuery('')}
                              >
                                <Combobox.Options className="absolute mt-1 max-h-60 w-full divide-y divide-gray-100 overflow-auto rounded-md bg-white z-[5] py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredPeople?.filter(
                                    (p) => !memberIds.includes(p.id),
                                  )?.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      {query !== ''
                                        ? 'Nothing found.'
                                        : 'All members are already assigned.'}
                                    </div>
                                  ) : (
                                    filteredPeople
                                      ?.filter((p) => !memberIds.includes(p.id))
                                      ?.map((person) => (
                                        <Combobox.Option
                                          key={person.id}
                                          className={({ active }) =>
                                            `relative cursor-default select-none py-1.5 pl-4 pr-4 ${
                                              active
                                                ? 'bg-teal-600 text-white'
                                                : 'text-gray-900'
                                            }`
                                          }
                                          value={person}
                                        >
                                          {({ active }) => (
                                            <span
                                              className={cn(
                                                'truncate flex items-center gap-3',
                                                active
                                                  ? 'text-white'
                                                  : 'text-gray-600',
                                              )}
                                            >
                                              {person?.avatar ? (
                                                <img
                                                  src={person?.avatar}
                                                  className="h-7 w-7 rounded-full"
                                                  alt=""
                                                />
                                              ) : (
                                                <UserCircleIcon className="w-8 h-8" />
                                              )}
                                              {person.given_name}{' '}
                                              {person?.family_name}
                                            </span>
                                          )}
                                        </Combobox.Option>
                                      ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                        )}
                      </div>
                    );
                  }}
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <button type="submit" disabled={saving} className="btn-primary">
              {singleTask ? 'Save changes' : 'Create new task'}
            </button>
          </div>
        </div>
      </div>
      {!isOwned && (
        <div className="grid items-center mt-4 mb-2">
          <p className="text-sm bg-white p-2 border border-gray-300">
            Please note that you cannot edit tasks created by others. You can
            only mark tasks as complete/incomplete.
          </p>
        </div>
      )}
    </form>
  );
}
